/**
 * Client targets (Clients → Targets) — ADR-001 §7, §11.8; guide §5.3 ("targets for the quarter").
 *
 * The prototype keys a target by a free-text platform name and a free-text SPOC name, two of which are not
 * people (ADR §7 data-quality note). Here a target belongs to a client and its owner is a USER ID; the importer
 * resolves a name to a client and a person, or rejects the row — free text is never stored as a person.
 *
 * The numbers are the prototype's (L2152): balance = max(0, target − so far); balance % = round(balance /
 * target × 100); Achieved when the balance is 0, Close when the balance % is 25 or less, Behind otherwise.
 * Visibility follows the client (managers all; a member their own). No revenue.
 */
import { z } from 'zod';
import { IsoDate, Timestamp, Uuid, Version, list } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
/** Indian financial-year quarter, `FY27-Q3` (Q1 = Apr–Jun, so FY27-Q3 = Oct–Dec 2026). */
export const Quarter = z
    .string()
    .regex(/^FY\d{2}-Q[1-4]$/, 'Write the quarter as FY27-Q3 (Q1 is April to June)')
    .meta({ id: 'Quarter' });
/** The financial-year quarter an IST date falls in: 2026-10-16 → FY27-Q3. */
export const quarterOf = (isoDate) => {
    const y = Number(isoDate.slice(0, 4));
    const m = Number(isoDate.slice(5, 7));
    const fy = m >= 4 ? y + 1 : y;
    const q = m >= 4 ? Math.floor((m - 4) / 3) + 1 : 4;
    return `FY${String(fy % 100).padStart(2, '0')}-Q${q}`;
};
export const TargetState = z.enum(['achieved', 'close', 'behind']).meta({ id: 'TargetState' });
export const TARGET_STATE_LABEL = { achieved: 'Achieved', close: 'Close', behind: 'Behind' };
/** Where the current figures came from: the sheet sync, a pasted import, or typed in. */
export const TargetSource = z.enum(['sheet', 'import', 'manual']).meta({ id: 'TargetSource' });
/** The prototype rule as code, so the SPA preview and the API agree. */
export function targetProgress(current, target) {
    const balance = Math.max(0, target - current);
    const pct = target > 0 ? Math.round((balance / target) * 100) : 0;
    const done_pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 100;
    return { balance, pct, done_pct, state: balance === 0 ? 'achieved' : pct <= 25 ? 'close' : 'behind' };
}
const Count = z.number().nonnegative().max(1e12);
export const ClientTarget = z
    .object({
    id: Uuid,
    client: ClientRef,
    /** The "Client platform" line; defaults to the client's name (a client may run more than one platform). */
    label: z.string(),
    quarter: Quarter,
    /** What "so far" counts, e.g. "Active users". */
    metric: z.string(),
    owner: UserRef,
    /** "User base". */
    base_value: Count.nullable(),
    /** "So far". */
    current_value: Count,
    target_value: Count.positive(),
    note: z.string().nullable(),
    source: TargetSource,
    /** The date the figures are as of ("figures as of 30 Sep"). */
    as_of: IsoDate,
    /** Derived (`targetProgress`): never stored. */
    balance: z.number().nonnegative(),
    /** Balance as a % of the target. */
    pct: z.number().int().min(0).max(100),
    /** So far as a % of the target, capped at 100 (the progress bar). */
    done_pct: z.number().int().min(0).max(100),
    state: TargetState,
    version: Version,
    updated_at: Timestamp,
    updated_by: UserRef.nullable(),
})
    .meta({ id: 'ClientTarget' });
const TargetSummary = z.object({
    /** "Platforms tracked". */
    tracked: z.number().int().nonnegative(),
    achieved: z.number().int().nonnegative(),
    close: z.number().int().nonnegative(),
    /** "Behind target" — also the hub badge. */
    behind: z.number().int().nonnegative(),
    /** "Balance still to deliver": Σ balance. */
    balance: z.number().nonnegative(),
    /** The latest `as_of` among the rows, for the source note. */
    as_of: IsoDate.nullable(),
});
export const ClientTargetList = list(ClientTarget).extend({ summary: TargetSummary }).meta({ id: 'ClientTargetList' });
export const TargetIdParams = z.object({ id: Uuid, tid: Uuid });
/** `GET /clients/{id}/targets` and `GET /targets`: default the current quarter. */
export const ListTargetsQuery = z.object({
    quarter: Quarter.optional(),
    state: TargetState.optional(),
    /** The SPOC filter ("All SPOCs" = omitted). Managers only; a member sees their own clients' targets. */
    owner_id: Uuid.optional(),
});
const Note = z.string().trim().max(500, 'Keep the note under 500 characters');
export const CreateTargetBody = z
    .object({
    quarter: Quarter,
    metric: z.string().trim().min(2).max(60).default('Active users'),
    label: z.string().trim().min(2).max(80).optional(),
    /** Must be a person who can own client work (a SPOC-eligible user); a name is not accepted. */
    owner_id: Uuid,
    base_value: Count.nullable().optional(),
    current_value: Count.default(0),
    target_value: Count.positive('The target must be more than 0'),
    note: Note.nullable().optional(),
})
    .strict();
/** "Update" (managers): so far, target, user base, note ("optional, e.g. why it is behind"), label, owner. */
export const UpdateTargetBody = z
    .object({
    current_value: Count.optional(),
    target_value: Count.positive('The target must be more than 0').optional(),
    base_value: Count.nullable().optional(),
    note: Note.nullable().optional(),
    label: z.string().trim().min(2).max(80).optional(),
    owner_id: Uuid.optional(),
})
    .strict()
    .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one thing' });
// ---------------------------------------------------------------- POST /targets/import ("Paste from sheet")
export const TARGET_IMPORT_MAX_ROWS = 500;
/**
 * Pasted rows, already split by the SPA (tab or comma): `Client, User base, So far, Target` or `Client, So far`.
 * A row matches an existing target by client (name, case-insensitive) + quarter + metric + label and updates
 * it. A client may run several platforms, each its own target with its own `label`: with `label` on the row it is
 * matched case-insensitively; without it, the client's single target for that quarter and metric is updated, and
 * when there are several the row is skipped as `target_ambiguous` (add the label column). No match adds a
 * target labelled as given, or with the client's name (as on create). Adding needs a target and an owner (`owner` on the row, else `default_owner_id` —
 * the "SPOC for new platforms" field). `owner` is a user's email or exact full name; anything else rejects
 * the row. `dry_run` returns the same result without writing, for the preview.
 */
export const ImportTargetsBody = z
    .object({
    quarter: Quarter,
    metric: z.string().trim().min(2).max(60).default('Active users'),
    default_owner_id: Uuid.optional(),
    dry_run: z.boolean().default(false),
    rows: z
        .array(z
        .object({
        client: z.string().trim().min(1).max(120),
        /** Which of the client's targets (platform), e.g. "Retail app". Omitted: the only one, or the client's name for a new one. */
        label: z.string().trim().min(2).max(80).optional(),
        base_value: Count.nullable().optional(),
        current_value: Count,
        target_value: Count.positive().optional(),
        owner: z.string().trim().min(1).max(120).optional(),
    })
        .strict())
        .min(1, 'Paste at least one row')
        .max(TARGET_IMPORT_MAX_ROWS, `Paste at most ${TARGET_IMPORT_MAX_ROWS} rows at a time`),
})
    .strict();
export const ImportSkipReason = z
    .enum(['client_not_found', 'client_ambiguous', 'target_ambiguous', 'owner_not_found', 'owner_not_eligible', 'target_missing', 'owner_missing', 'not_in_scope'])
    .meta({ id: 'ImportSkipReason' });
export const ImportTargetsResponse = z
    .object({
    dry_run: z.boolean(),
    /** The toast: "N updated, N added, N skipped". */
    updated: z.number().int().nonnegative(),
    added: z.number().int().nonnegative(),
    skipped: z.array(z.object({ row: z.number().int().positive(), client: z.string(), reason: ImportSkipReason, message: z.string() })),
    /** One line per pasted row, in order: what happened (or would, on a dry run) and to which target. */
    rows: z.array(z.object({ row: z.number().int().positive(), action: z.enum(['update', 'add', 'skip']), target_id: Uuid.nullable() })),
    /** The targets written; empty on a dry run. */
    targets: z.array(ClientTarget),
})
    .meta({ id: 'ImportTargetsResponse' });
//# sourceMappingURL=targets.js.map